import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { IdentificationCard } from "@phosphor-icons/react";
import { Spinner, Pill, Avatar, STATUS_TONE, isHr } from "../components/hr/hrUtils";
import { PersonalCard, AddressesCard, ContactsCard } from "../components/hr/EmployeeCards";
import { EmploymentCard, RightToWorkCard } from "../components/hr/EmploymentCards";
import { DocumentsCard } from "../components/hr/DocumentsCards";
import { ChecklistsCard } from "../components/hr/ChecklistsCard";
import { PayCard } from "../components/hr/PayCard";
import { LeaveCard } from "../components/hr/LeaveCard";
import { TeamLeaveCard } from "../components/hr/TeamLeaveCard";
import { MyTeamCard } from "../components/hr/MyTeamCard";
import { TeamCalendarCard } from "../components/hr/TeamCalendarCard";
import { SicknessCard } from "../components/hr/SicknessCard";
import { ReviewsCard } from "../components/hr/ReviewsCard";
import { TrainingCard } from "../components/hr/TrainingCard";
import { Clock } from "@phosphor-icons/react";

/** /hr/me and /my-work/hr — a staff member's own HR record: keep contact details, addresses and next of kin current, upload ID and certificates. */
export default function MyHrPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const load = useCallback(() => API.get("/hr/me").then((r) => setData(r.data)).catch(() => setData({ record: null })), []);
  useEffect(() => { load(); API.get("/hr/meta").then((r) => setMeta(r.data)).catch(() => setMeta(null)); }, [load]);
  if (!data) return <Spinner />;
  const emp = data.record;
  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="my-hr-page">
      <div className="flex flex-wrap items-center gap-3">
        <Avatar name={emp?.name || user?.name} size="lg" />
        <div className="flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">My HR</p>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">{emp?.display_name || user?.name} {emp && <Pill tone={STATUS_TONE[emp.status]}>{emp.status_label}</Pill>}</h1>
          <p className="text-sm text-muted-foreground">{emp ? [emp.job_title, emp.department, emp.employee_number].filter(Boolean).join(" · ") : "Your personal details, next of kin and documents"}{emp?.manager_name ? ` · reports to ${emp.manager_name}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/hr/clock" className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted" data-testid="my-hr-clock"><Clock size={14} /> Clock in / out</Link>
          {isHr(user) && <Link to="/hr" className="text-xs text-primary underline" data-testid="my-hr-to-hub">Open the HR hub</Link>}
        </div>
      </div>
      <MyTeamCard />
      <TeamCalendarCard />
      <TeamLeaveCard highlight={params.get("team") === "1"} />
      {!emp ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center" data-testid="my-hr-none">
          <IdentificationCard size={28} className="mx-auto text-muted-foreground" />
          <p className="text-sm font-medium mt-2">HR hasn't created your record yet</p>
          <p className="text-xs text-muted-foreground mt-1">Once it exists you'll be able to keep your address, phone numbers and emergency contacts up to date here and upload your ID, right-to-work and certificates.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground rounded-md border border-border bg-muted/30 px-3 py-2" data-testid="my-hr-hint">You can update your contact details, addresses and emergency contacts yourself — HR is told when you do. Ask HR to change anything else.</p>
          <PersonalCard emp={emp} meta={meta} hr={false} onSaved={(e) => setData({ ...data, record: e })} />
          <PayCard />
          <LeaveCard />
          <SicknessCard onChanged={load} />
          <div className="grid lg:grid-cols-2 gap-4"><AddressesCard emp={emp} hr={false} onSaved={(e) => setData({ ...data, record: e })} /><ContactsCard emp={emp} hr={false} onSaved={(e) => setData({ ...data, record: e })} /></div>
          <EmploymentCard emp={emp} meta={meta} hr={false} />
          <RightToWorkCard emp={emp} meta={meta} hr={false} />
          <DocumentsCard emp={emp} meta={meta} hr={false} onSaved={load} />
          <ChecklistsCard emp={emp} hr={false} onEmployeeChange={load} />
          <ReviewsCard emp={emp} hr={false} onEmployeeChange={load} />
          <TrainingCard emp={emp} hr={false} />
        </div>
      )}
    </div>
  );
}
